/**
 * 向父窗口发送消息
 * @module send-parent-message
 */

/**
 * 消息类型定义
 */
export interface ParentWindowMessage<T = any> {
    type: string;
    data: T;
}

/**
 * 任务变化消息数据
 */
export interface TaskChangeData {
    name: string;
    taskId: string;
}

/**
 * 路由变化消息数据
 */
export interface RouterChangeData {
    path?: string;
    name?: string;
    params?: Record<string, any>;
    query?: Record<string, any>;
    openInNewWindow?: boolean
    useNativeNavigation?: boolean
}

/**
 * 向父窗口发送消息
 * @param message - 要发送的消息对象
 * @param targetOrigin - 目标源，默认为 '*'
 */
export function sendParentMessage<T = any>(
    message: ParentWindowMessage<T>,
    targetOrigin: string = '*',
): void {
    const messageString = JSON.stringify(message);
    window.parent.postMessage(messageString, targetOrigin);
}

/**
 * 向父窗口发送任务变化消息
 * @param routeName - 路由名称
 * @param taskId - 任务ID
 */
export function sendTaskChangeMessage(routeName: string, taskId: string): void {
    sendParentMessage<TaskChangeData>({
        type: 'task-change',
        data: {
            name: routeName,
            taskId,
        },
    });
}

/**
 * 向父窗口发送路由变化消息，支持任意路由跳转
 * @param routeData - 路由数据，支持 path 或 name 方式跳转
 * @example
 * // 使用 path 跳转
 * sendRouterChangeMessage({ path: '/home/dashboard', query: { tab: 'overview' } })
 *
 * // 使用 name 跳转
 * sendRouterChangeMessage({ name: 'UserProfile', params: { userId: '123' }, query: { section: 'settings' } })
 */
export function sendRouterChangeMessage(routeData: RouterChangeData): void {
    sendParentMessage<RouterChangeData>({
        type: 'router-change',
        data: routeData,
    });
}
